import axios, {AxiosInstance} from 'axios';

const apiClient: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_GIFBUZ_API,
});

//== Only attach the header when a token actually exists. getToken() returns '' for logged
//== out visitors and axios keeps empty-string headers, so we were sending `authorization: `
//== on public requests. Authorization is never CORS-safelisted regardless of its value, so
//== that empty header forced an OPTIONS preflight — a whole extra round trip in front of
//== the home page's first data fetch, for every anonymous visitor.
const authHeader = (headerToken: any) =>
    headerToken ? {headers: {'authorization': `${headerToken}`}} : undefined;

//== Get All Categories
export const getAllCategories = async () => {
    const cat: any = await apiClient.get('/search/home-categories/0/100');
    return cat?.data?.categories;
}

//== Get Home Page Stickers
export const getStickers = async () => {
    const res: any = await apiClient.get('/search/resource/sticker/0/10');
    return res?.data?.resources;
}

//== Get Infinite Scroll GIFs
export const getScrollGifs = async (apiPath: string, pageParam: number = 0, itemsPerPage: any, headerToken: any) => {
    const res: any = await apiClient.get(`${apiPath}/${pageParam}/${itemsPerPage}`, authHeader(headerToken));
    return res?.data;
}

//== Get Infinite Scroll Artists
export const getScrollArtists = async (apiPath: string, pageParam: number = 0, itemsPerPage: any, orderBy: string, sortBy: string) => {
    const res: any = await apiClient.get(`${apiPath}/${pageParam}/${itemsPerPage}?order=${orderBy}&sort=${sortBy}`);
    return res?.data;

}

//== Get Slider Sticker or Categories
export const getSliderItems = async (apiPath: string, limit: number, headerToken: any) => {
    const res: any = await apiClient.get(`${apiPath}/0/${limit}`, authHeader(headerToken));
    return res?.data;
}

//== Get single page resource data
export const getSingleResourceData = async (slug: any, options = {}) => {
    const res: any = await apiClient.get(`/resources/slug/${encodeURIComponent(slug)}`, options);
    return res?.data;
};

//== Get single page resource data
export const getArtistData = async (username: any, options = {}) => {
    return await apiClient.get(`public-profile/username/${username}`, options);
};

//== Get public user profile information
export const getPublicProfileInfo = async (ownerId: any) => {
    const res: any = await apiClient.get(`/public-profile/${ownerId}`);
    return res?.data;
};

//== User Login
export const userLogin = (loginData: any) => {
    const res: any = apiClient.post(`${process.env.REACT_APP_END_POINT_LOGIN}`, loginData);
    return res;
}
//== User Sign up
export const userSignUp = (signUpData: any) => {
    const res: any = apiClient.post(`${process.env.REACT_APP_END_POINT_REGISTER}`, signUpData);
    return res;
}
//== User Data
export const userData = async (headerToken: any) => {
    return await apiClient.get(`${process.env.REACT_APP_END_POINT_USER_PROFILE}`, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== Update User
export const updateUser = async (headerToken: any, userData: any) => {
    return await apiClient.patch(`${process.env.REACT_APP_END_POINT_USER_PROFILE}`, userData, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}
//== Add / Update User profile photo
export const updateUserProfilePhotos = async (headerToken: any, apiEndPoints: any, imageData: any) => {
    return await apiClient.put(`/user/upload/${apiEndPoints}`, imageData, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== Delete User profile photo
export const deleteUserProfilePic = async (headerToken: string) => {
    return await apiClient.delete(`/artist/profile/remove-profile-photo`,{
        headers: {
            'authorization': `${headerToken}`
        }
    })
};


//== Delete User
export const deleteUser = async (headerToken: any) => {
    return await apiClient.delete(`/user/delete`, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== Favorite Gif
export const favoriteGif = async (gifId: any, headerToken: any) => {
    return await apiClient.patch(`/user/save-favourite-image/${gifId}/web`, {}, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== unfavorite Gif
export const unfavoriteGif = async (gifId: any, headerToken: any) => {
    return await apiClient.patch(`/user/unfavourite-image/${gifId}/web`, {}, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== Get Report Items
export const getReportItems = async () => {
    const res: any = await apiClient.get(`/resources/report-categories`);
    return res?.data;
};

//== Submit report data
export const submitReport = async (headerToken: any, reportBody: any) => {
    return await apiClient.post('/resources/report-by-user', reportBody, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== Submit Upload GIF
export const submitUploadGif = async (uploadData: any, config: any = {}) => {
    return await apiClient.post('/resources/save', uploadData, config);
}

//== Local video file conversion to GIF
export const conversionLocalVideoFile = async (fileData: any, config: any = {}) => {
    return await apiClient.post('/utility/convert-local-file-to-gif-s3', fileData, config);
}

//== Url video file conversion to GIF
export const conversionUrlVideoFile = async (fileData: any, config: any = {}) => {
    return await apiClient.post('/utility/convert-file-to-gif-by-url', fileData, config);
}

//== Submit Create Gif
export const submitCreateGif = async (endPoints: string, CreateData: any, config: any = {}) => {
    return await apiClient.post(`${endPoints}`, CreateData, config);
}

//== Check transparency gif
export const checkTransparency = async (headerToken: any, fileObj: any = {}) => {
    return await apiClient.post('/resources/sticker-upload', fileObj, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== Delete Resource
export const deleteSingleResource = async (headerToken: any, resourceId: any) => {
    return await apiClient.delete(`/resources/delete/${resourceId}`, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}

//== Url check and file download
export const urlFileDownload = async (headerToken: any, fileValue: any) => {
    return await apiClient.post('/utility/download/file', fileValue, {
        headers: {
            'authorization': `${headerToken}`,
            'Access-Control-Allow-Origin': '*'
        }
    });
}

//== Forgot Password Send Otp
export const forgotPassOtp = async (emailData: any) => {
    return await apiClient.post(`${process.env.REACT_APP_END_POINT_SEND_OTP}`, emailData);
}

//== Forgot Password Send Otp
export const forgotPassSubmit = async (forgotChangeData: any) => {
    return await apiClient.post(`${process.env.REACT_APP_END_POINT_RESET_PASSWORD}`, forgotChangeData);
}

//== Search Filter API
export const searchFilter = async (queryValue: any, headerToken: any) => {
    return await apiClient.get(`/search/find-similar-tags/${queryValue}`, {
        headers: {
            'authorization': `${headerToken}`
        }
    });
}
//== Trending SearchTags API
export const trendingTags = async () => {
    return await apiClient.get(`/search/trending-tag/`);
}

export default apiClient;